import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, EmptyState, Icon, List, ListItem, LoadingState, StatusPill, Callout } from './ui'
import { timelineApi } from '../api/timeline'
import type { ContractTimeline as ContractTimelineData } from '../api/timeline'

/**
 * F-058 (TASK-0312): "Cronologia" — unified, newest-first timeline of a contract (status changes,
 * notes, documents, tasks, appointments, lotto, operator outcomes, audited actions). Times are shown
 * in Europe/Rome. Never shows amounts: the server does not send any.
 */
const ROME = 'Europe/Rome'

function formatRome(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    timeZone: ROME,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const KIND_ICONS: Record<string, string> = {
  stato_iniziale: 'inventory_2',
  stato_cambiato: 'swap_horiz',
  nota: 'sticky_note_2',
  lotto_assegnato: 'inventory',
  esito_operatore: 'fact_check',
  documento_caricato: 'upload_file',
  documento_scaricato: 'download',
  documento_anteprima: 'visibility',
  task_creato: 'add_task',
  task_completato: 'task_alt',
  appuntamento_fissato: 'event',
  lead_origine: 'person_search',
  attribuzione_modificata: 'badge',
  importi_aggiornati: 'edit_note',
  incasso_registrato: 'payments',
  prodotto_modificato: 'category',
}

export function ContractTimeline({ contractId }: { contractId: number }) {
  const [data, setData] = useState<ContractTimelineData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!Number.isFinite(contractId)) return
    let cancelled = false
    setLoading(true)
    setError(null)
    timelineApi.forContract(contractId)
      .then(d => { if (!cancelled) setData(d) })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Errore nel caricamento della cronologia') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [contractId])

  return (
    <Card variant="outlined" padding="none" className="mt-3">
      <CardHeader
        icon="timeline"
        title="Cronologia"
        actions={data ? <StatusPill tone="neutral">{data.items.length}</StatusPill> : undefined}
      />
      <CardContent className="p-0">
        {loading ? (
          <LoadingState label="Caricamento..." />
        ) : error ? (
          <Callout tone="error" className="m-3">{error}</Callout>
        ) : !data || data.items.length === 0 ? (
          <EmptyState icon="timeline" title="Nessun evento registrato" compact />
        ) : (
          <div className="md-scroll-box">
          <List aria-label="Cronologia della pratica">
            {data.items.map((it, i) => (
              <ListItem
                key={`${it.kind}-${it.refType ?? ''}-${it.refId ?? ''}-${it.occurredAt}-${i}`}
                leading={<Icon name={KIND_ICONS[it.kind] ?? 'circle'} size={20} />}
                headline={
                  <>
                    {it.title}
                    {it.isSnapshot && <StatusPill tone="neutral" className="ms-2">Fotografia iniziale</StatusPill>}
                  </>
                }
                supporting={
                  <>
                    {formatRome(it.occurredAt)}
                    {it.actorName ? ` - ${it.actorName}` : it.actorUserId ? ` - Utente #${it.actorUserId}` : ''}
                  </>
                }
              />
            ))}
          </List>
          </div>
        )}
        {data?.truncated && (
          <div className="p-2 text-center text-muted md-typescale-body-small border-top">
            Mostrati i 500 eventi piu' recenti.
          </div>
        )}
      </CardContent>
    </Card>
  )
}
